import { Routes, Route, Navigate } from "react-router-dom";
import { AppShell } from "./components/AppShell";
import { ProtectedRoute } from "./components/ProtectedRoute";
import { useAuth } from "./lib/auth";

import { Login } from "./screens/Login";
import { Dashboard } from "./screens/Dashboard";
import { Pos } from "./screens/Pos";
import { RepairOrderNew } from "./screens/RepairOrderNew";
import { RepairOrderDetail } from "./screens/RepairOrderDetail";
import { RepairOrderPayment } from "./screens/RepairOrderPayment";
import { OrderHistory } from "./screens/OrderHistory";
import { OrderDetail } from "./screens/OrderDetail";
import { Inventory } from "./screens/Inventory";
import { Receiving } from "./screens/Receiving";
import { Shipping } from "./screens/Shipping";
import { ServicePrograms } from "./screens/ServicePrograms";
import { Oversight } from "./screens/Oversight";
import { Reports } from "./screens/Reports";
import { Alerts } from "./screens/Alerts";
import { CustomerPortal } from "./screens/portal/CustomerPortal";

function Shell({ title, subtitle, children }: { title: string; subtitle?: string; children: React.ReactNode }) {
  return <AppShell title={title} subtitle={subtitle}>{children}</AppShell>;
}

export default function App() {
  const { user } = useAuth();

  return (
    <Routes>
      <Route path="/login" element={user ? <Navigate to="/dashboard" replace /> : <Login />} />
      <Route path="/portal/*" element={<CustomerPortal />} />

      <Route
        path="/dashboard"
        element={
          <ProtectedRoute permission="dashboard.read">
            <Shell title={`Welcome back, ${user?.name.split(" ")[0] ?? "there"}`} subtitle="Service center performance at a glance">
              <Dashboard />
            </Shell>
          </ProtectedRoute>
        }
      />
      <Route
        path="/pos"
        element={
          <ProtectedRoute permission="pos.create_order">
            <Shell title="Point of Sale" subtitle="Ring up a sale, return, or warranty exchange">
              <Pos />
            </Shell>
          </ProtectedRoute>
        }
      />
      <Route
        path="/repair-orders/new"
        element={
          <ProtectedRoute permission="repair_orders.create">
            <Shell title="New Repair Order" subtitle="Intake a tool for service">
              <RepairOrderNew />
            </Shell>
          </ProtectedRoute>
        }
      />
      <Route
        path="/repair-orders/:id"
        element={
          <ProtectedRoute permission="repair_orders.work">
            <Shell title="Repair Order" subtitle="Work order, safety checks, and parts used">
              <RepairOrderDetail />
            </Shell>
          </ProtectedRoute>
        }
      />
      <Route
        path="/repair-orders/:id/payment"
        element={
          <ProtectedRoute permission="repair_orders.work">
            <Shell title="Repair Completion & Payment" subtitle="Collect payment and close the order">
              <RepairOrderPayment />
            </Shell>
          </ProtectedRoute>
        }
      />
      <Route
        path="/orders"
        element={
          <ProtectedRoute permission="order_history.read">
            <Shell title="Order History & Notifications" subtitle="Search, review, and notify customers">
              <OrderHistory />
            </Shell>
          </ProtectedRoute>
        }
      />
      <Route
        path="/orders/:orderNumber"
        element={
          <ProtectedRoute permission="order_history.read">
            <Shell title="Order Detail" subtitle="Full order record">
              <OrderDetail />
            </Shell>
          </ProtectedRoute>
        }
      />
      <Route
        path="/inventory"
        element={
          <ProtectedRoute permission="inventory.read">
            <Shell title="Parts Inventory" subtitle="Stock levels, bins, and part detail">
              <Inventory />
            </Shell>
          </ProtectedRoute>
        }
      />
      <Route
        path="/receiving"
        element={
          <ProtectedRoute permission="receiving.receive">
            <Shell title="Receiving" subtitle="Cartons, PO reconciliation, and bin putaway">
              <Receiving />
            </Shell>
          </ProtectedRoute>
        }
      />
      <Route
        path="/shipping"
        element={
          <ProtectedRoute permission="shipping.manage">
            <Shell title="Shipping" subtitle="Ready-to-ship queue and productivity">
              <Shipping />
            </Shell>
          </ProtectedRoute>
        }
      />
      <Route
        path="/service-programs"
        element={
          <ProtectedRoute permission="service_programs.read">
            <Shell title="Service Programs" subtitle="Program catalog and GRC pricing">
              <ServicePrograms />
            </Shell>
          </ProtectedRoute>
        }
      />
      <Route
        path="/oversight"
        element={
          <ProtectedRoute permission="management_oversight.approve_pricing">
            <Shell title="Approvals & Oversight" subtitle="Scrap, special pricing, and fraud review">
              <Oversight />
            </Shell>
          </ProtectedRoute>
        }
      />
      <Route
        path="/reports"
        element={
          <ProtectedRoute permission="reports.read">
            <Shell title="Daily Operational Reporting" subtitle="Scheduled report runs">
              <Reports />
            </Shell>
          </ProtectedRoute>
        }
      />
      <Route
        path="/alerts"
        element={
          <ProtectedRoute permission="dashboard.read">
            <Shell title="Alerts" subtitle="Outbound notification activity">
              <Alerts />
            </Shell>
          </ProtectedRoute>
        }
      />

      <Route path="/" element={<Navigate to="/dashboard" replace />} />
      <Route path="*" element={<Navigate to="/dashboard" replace />} />
    </Routes>
  );
}
